import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { HERO_IMAGE } from "../lib/media";

const line = {
    hidden: { y: "110%" },
    show: (i = 0) => ({
        y: "0%",
        transition: { duration: 1.15, ease: [0.19, 1, 0.22, 1], delay: 0.2 + i * 0.12 },
    }),
};

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

    return (
        <section
            ref={ref}
            id="hero"
            className="relative min-h-[100svh] w-full overflow-hidden bg-ink-950 grain-overlay"
            data-testid="hero-section"
        >
            {/* Parallax image behind text — Francesco portrait */}
            <motion.div
                aria-hidden
                style={{ y: imgY, scale: imgScale }}
                className="absolute top-[38%] md:top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] sm:w-[55vw] md:w-[40vw] max-w-[520px] aspect-[3/4] max-h-[55vh] md:max-h-[62vh] z-0"
            >
                <div className="w-full h-full overflow-hidden">
                    <img
                        src={HERO_IMAGE}
                        alt="Francesco Ciotoli — Fotografo & Videomaker"
                        className="w-full h-full object-cover object-[50%_15%] md:object-[center_25%] opacity-55"
                    />
                </div>
                <div className="absolute inset-0 ring-1 ring-white/10 pointer-events-none" />
            </motion.div>

            {/* Top meta */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="absolute top-20 md:top-24 left-5 md:left-10 z-30 overline text-[9px] md:text-[11px] max-w-[45vw] mix-blend-difference text-bone-50"
                data-testid="hero-tag-left"
            >
                <span className="text-flash">●</span> IN THE FRAME<br className="md:hidden"/><span className="hidden md:inline"> — </span>SINCE 2019
            </motion.div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="absolute top-20 md:top-24 right-5 md:right-10 z-30 overline text-right text-[9px] md:text-[11px] max-w-[45vw] mix-blend-difference text-bone-50"
                data-testid="hero-tag-right"
            >
                CECCANO · FR<br /><span className="hidden sm:inline">N 41.57° · E 13.34°</span>
            </motion.div>

            {/* Kinetic type */}
            <motion.div
                style={{ y: textY }}
                className="relative z-10 flex flex-col justify-end min-h-[100svh] px-5 md:px-10 pt-32 md:pt-0 pb-16 md:pb-28"
                data-testid="hero-heading-wrap"
            >
                <h1
                    className="font-display uppercase text-bone-50 leading-[0.82] tracking-[-0.04em] select-none"
                    style={{ fontSize: "clamp(2.75rem, 13vw, 15rem)" }}
                    data-testid="hero-heading"
                >
                    <span className="line-mask">
                        <motion.span variants={line} initial="hidden" animate="show" custom={0} className="block">
                            Francesco
                        </motion.span>
                    </span>
                    <span className="line-mask">
                        <motion.span variants={line} initial="hidden" animate="show" custom={1} className="block">
                            Ciotoli<span className="text-flash">.</span>
                        </motion.span>
                    </span>
                    <span className="line-mask">
                        <motion.span
                            variants={line}
                            initial="hidden"
                            animate="show"
                            custom={2}
                            className="block font-serif-i normal-case tracking-normal text-bone-400"
                            style={{ fontSize: "clamp(1.1rem, 4vw, 4rem)", lineHeight: 1 }}
                        >
                            fotografo &amp; <span className="text-bone-50">videomaker</span>
                        </motion.span>
                    </span>
                </h1>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.85, duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                    className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 items-end"
                >
                    <p className="md:col-span-6 text-bone-200 max-w-lg text-base md:text-lg leading-relaxed font-light" data-testid="hero-subtitle">
                        Racconto persone, sport, editoriali e paesaggi con
                        <span className="font-serif-i text-bone-50"> luce e movimento</span>.
                        Fotografia, video, drone e post-produzione — da Ceccano (FR) per tutta Italia.
                    </p>
                    <div className="md:col-span-6 flex flex-col md:items-end gap-3">
                        <a
                            href="#work"
                            data-cursor="view"
                            onClick={(e) => { e.preventDefault(); document.getElementById("work")?.scrollIntoView({ behavior: "smooth" }); }}
                            className="btn-flash"
                            data-testid="hero-cta-portfolio"
                        >
                            <span>Guarda il lavoro</span>
                            <span>→</span>
                        </a>
                        <span className="overline">Scroll ↓</span>
                    </div>
                </motion.div>
            </motion.div>
        </section>
    );
}
